import * as React from "react";
import { ChevronDown, ChevronRight, Copy, GitBranch, LoaderCircle, Pencil } from "lucide-react";
import { toast } from "react-toastify";

import {
  useCopyContractFamilyMutation,
  useDeleteContractFamilyCopyMutation,
  useRenameContractFamilyMutation,
} from "@/api/contract-families";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TableCell, TableRow } from "@/components/ui/table/table";

import { ContractFamilyDocumentViewModel, ContractFamilyGroupViewModel } from "../contractFamilyViewModels";
import { ContractFamilyDocumentRow, ContractFamilyMembershipChange } from "./ContractFamilyDocumentRow";

interface Props {
  family: ContractFamilyGroupViewModel;
  families: ContractFamilyGroupViewModel[];
  selectedDocumentId: string | null;
  onSelectDocument: (document: ContractFamilyDocumentViewModel) => void;
  onMembershipChanged?: (change: ContractFamilyMembershipChange) => void;
  pipelineRunId?: string | null;
  onPipelineDocumentExcluded?: () => void;
  expanded: boolean;
  onToggle: () => void;
  readOnly?: boolean;
}

export const ContractFamilyRow: React.FC<Props> = ({
  family,
  families,
  selectedDocumentId,
  onSelectDocument,
  onMembershipChanged,
  pipelineRunId,
  onPipelineDocumentExcluded,
  expanded,
  onToggle,
  readOnly = false,
}) => {
  const [copyFamily, copyState] = useCopyContractFamilyMutation();
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const [copiedFamilyId, setCopiedFamilyId] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteCopy, deleteState] = useDeleteContractFamilyCopyMutation();
  const [renameFamily, renameState] = useRenameContractFamilyMutation();
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(family.name);
  const [renameError, setRenameError] = React.useState<string | null>(null);
  const handleRequestDeleteCopy = () => setDeleteDialogOpen(true);

  const handleCopyFamily = async () => {
    setCopyError(null);
    try {
      const copied = await copyFamily(family.familyId).unwrap();
      setCopiedFamilyId(copied.copied_family_id);
      toast.success(
        <span>
          Family copied.{" "}
          <button type="button" className="font-semibold underline" onClick={handleRequestDeleteCopy}>
            Delete copy
          </button>
        </span>,
        { autoClose: false },
      );
    } catch {
      setCopyError("Copy failed");
      toast.error("Unable to copy family.");
    }
  };

  const saveName = async () => {
    const normalized = name.trim();
    if (!normalized) return;
    setRenameError(null);
    try {
      await renameFamily({ familyId: family.familyId, name: normalized }).unwrap();
      setEditing(false);
    } catch {
      setRenameError("Unable to rename family.");
    }
  };

  const handleDeleteCopy = async () => {
    if (!copiedFamilyId) return;
    try {
      await deleteCopy(copiedFamilyId).unwrap();
      setCopiedFamilyId(null);
      setDeleteDialogOpen(false);
    } catch {
      setCopyError("Unable to delete copy.");
      toast.error("Unable to delete copied family.");
    }
  };
  const handleCancelRename = () => {
    setEditing(false);
    setName(family.name);
  };

  return (
    <>
      <TableRow className="h-11 bg-slate-50 hover:bg-slate-50">
        <TableCell colSpan={4} className="px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              className="flex min-w-0 items-center gap-2 text-left"
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <GitBranch className="h-4 w-4 shrink-0 text-slate-500" />
              {!readOnly && editing ? (
                <input
                  aria-label="Family name"
                  maxLength={255}
                  value={name}
                  onClick={event => event.stopPropagation()}
                  onChange={event => setName(event.target.value)}
                  className="rounded border px-2 py-1 text-sm"
                />
              ) : (
                <span className="truncate text-sm font-semibold text-slate-950">{family.name}</span>
              )}
            </button>
            <div className="flex shrink-0 items-center gap-2">
              {copyError ? (
                <span role="status" className="text-xs text-red-700">
                  {copyError}
                </span>
              ) : null}
              <span className="text-xs text-slate-500">
                {family.documentCount} {family.documentCount === 1 ? "document" : "documents"}
              </span>
              {!readOnly ? (
                editing ? (
                  <>
                    <Button type="button" size="xs" disabled={renameState.isLoading || !name.trim()} onClick={saveName}>
                      Save
                    </Button>
                    <Button type="button" size="xs" variant="outline" onClick={handleCancelRename}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    aria-label={`Rename ${family.name}`}
                    onClick={() => setEditing(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )
              ) : null}
              {!readOnly ? (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  disabled={copyState.isLoading}
                  onClick={handleCopyFamily}
                >
                  {copyState.isLoading ? (
                    <LoaderCircle aria-hidden="true" className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Copy aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
                  )}
                  Copy family
                </Button>
              ) : null}
            </div>
          </div>
          {renameError ? (
            <p role="alert" className="mt-1 text-xs text-red-700">
              {renameError}
            </p>
          ) : null}
        </TableCell>
      </TableRow>
      {!readOnly ? (
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent aria-describedby="delete-copy-description">
            <DialogHeader>
              <DialogTitle>Delete copied family?</DialogTitle>
              <DialogDescription id="delete-copy-description">
                This removes only the copy created from {family.name}. The source family is unchanged.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={deleteState.isLoading || copiedFamilyId === null}
                onClick={() => void handleDeleteCopy()}
              >
                Delete copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
      {expanded && family.master && (
        <ContractFamilyDocumentRow
          document={family.master}
          selected={family.master.documentId === selectedDocumentId}
          families={families}
          onSelectDocument={onSelectDocument}
          onMembershipChanged={onMembershipChanged}
          pipelineRunId={pipelineRunId}
          onPipelineDocumentExcluded={onPipelineDocumentExcluded}
          readOnly={readOnly}
        />
      )}
      {expanded &&
        family.childDocuments.map(document => (
          <ContractFamilyDocumentRow
            key={document.documentId}
            document={document}
            selected={document.documentId === selectedDocumentId}
            indent={true}
            families={families}
            onSelectDocument={onSelectDocument}
            onMembershipChanged={onMembershipChanged}
            pipelineRunId={pipelineRunId}
            onPipelineDocumentExcluded={onPipelineDocumentExcluded}
            readOnly={readOnly}
          />
        ))}
    </>
  );
};
